<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object, post: Object, relatedPosts: { type: Array, default: () => [] },
    service: String, branding: Object, designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const serviceLabel = props.service === 'pv' ? 'Photovoltaik' : 'Wärmepumpe';
const publishedDate = props.post.published_at ? new Date(props.post.published_at).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
const heroImg = props.post.featured_image ? `/media/${props.post.featured_image}` : (props.spotImages?.blog_hero || props.spotImages?.welcome_hero || null);
// post.author can be an Eloquent relation object — coerce safely.
const resolvedAuthorName = (p) => {
    if (typeof p.author_name === 'string' && p.author_name.length) return p.author_name;
    if (typeof p.author === 'string' && p.author.length) return p.author;
    if (p.author && typeof p.author === 'object' && typeof p.author.name === 'string') return p.author.name;
    return t('blogpost_author_default', 'Redaktion');
};
const authorName = computed(() => resolvedAuthorName(props.post));
const authorInitials = computed(() => String(authorName.value).split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join(''));

const readingProgress = ref(0);
let rafId = null;
const updateProgress = () => { const a = document.querySelector('.sv-article'); if (!a) return; const t = a.getBoundingClientRect().top + window.scrollY; const h = a.offsetHeight; const s = window.scrollY - t; readingProgress.value = Math.min(100, Math.max(0, (s / (h - window.innerHeight)) * 100)); rafId = null; };
const onScroll = () => { if (!rafId) rafId = requestAnimationFrame(updateProgress); };
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }));
onUnmounted(() => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); });
</script>

<template>
    <Head :title="post.title">
        <meta v-if="post.meta_description" :content="post.meta_description" name="description" />
        <meta :content="post.title" property="og:title" />
        <meta content="article" property="og:type" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="blog" heroMode>
        <section class="sv-blogpost-hero">
            <div class="sv-blogpost-hero__bg" aria-hidden="true"></div>
            <div class="sv-blogpost-hero__content">
                <nav class="sv-blogpost-hero__bread" aria-label="Breadcrumb">
                    <Link href="/">{{ t('blogpost_bread_home', 'Startseite') }}</Link> / <Link href="/ratgeber">{{ t('blogpost_bread_blog', 'Ratgeber') }}</Link>
                </nav>
                <div v-if="post.tags && post.tags.length" class="sv-blogpost-hero__tags">
                    <span v-for="tag in post.tags" :key="tag" class="sv-blogpost-hero__tag-pill">{{ tag }}</span>
                </div>
                <h1 class="sv-blogpost-hero__title">{{ post.title }}</h1>
                <p v-if="post.excerpt" class="sv-blogpost-hero__excerpt">{{ post.excerpt }}</p>
                <div class="sv-blogpost-hero__meta">
                    <span>{{ authorName }}</span>
                    <span v-if="publishedDate"> - {{ publishedDate }}</span>
                    <span v-if="post.reading_time"> - {{ post.reading_time }} {{ t('blogpost_reading_minutes', 'Min. Lesezeit') }}</span>
                </div>
            </div>
        </section>

        <section class="sv-blogpost-img-section">
            <div class="sv-blogpost-img-section__inner">
                <img :src="heroImg" :alt="post.title" class="sv-blogpost-img" loading="eager" />
            </div>
        </section>

        <section class="sv-blogpost-funnel-strip">
            <div class="sv-blogpost-funnel-strip__inner">
                <div class="sv-funnel-wrap"><LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" /></div>
            </div>
        </section>

        <div class="sv-reading-bar" aria-hidden="true"><div class="sv-reading-bar__fill" :style="{ width: readingProgress + '%' }"></div></div>

        <article class="sv-article">
            <div class="sv-article__inner">
                <div class="sv-article__card">
                    <div class="sv-article__byline">
                        <div class="sv-article__byline-avatar">{{ authorInitials }}</div>
                        <div class="sv-article__byline-info">
                            <span class="sv-article__byline-name">{{ authorName }}</span>
                            <time v-if="publishedDate" class="sv-article__byline-date">{{ publishedDate }}</time>
                        </div>
                    </div>
                    <div class="sv-article__prose" v-html="post.body" />
                </div>

                <div v-if="relatedPosts && relatedPosts.length" class="sv-related">
                    <h2 class="sv-related__heading">{{ t('blogpost_related_heading', 'Weitere Artikel') }}</h2>
                    <div class="sv-related__grid">
                        <Link v-for="r in relatedPosts.slice(0, 3)" :key="r.id" :href="`/ratgeber/${r.slug}`" class="sv-related__card">
                            <div v-if="r.featured_image" class="sv-related__card-img-wrap"><img :src="r.featured_image" :alt="r.title" class="sv-related__card-img" loading="lazy" /></div>
                            <div class="sv-related__card-body">
                                <h3 class="sv-related__card-title">{{ r.title }}</h3>
                            </div>
                        </Link>
                    </div>
                </div>

                <div class="sv-article__disclaimer">
                    <p><strong>{{ t('blogpost_disclaimer_label', 'Redaktioneller Hinweis:') }}</strong> {{ t('blogpost_disclaimer_body', 'Dieser Artikel wurde mit KI-Unterstützung erstellt und von unserer Fachredaktion geprüft. Die Inhalte dienen der allgemeinen Information und ersetzen keine individuelle Fachberatung.') }}</p>
                </div>
            </div>
        </article>

        <section class="sv-blogpost-cta">
            <div class="sv-blogpost-cta__inner">
                <h2 class="sv-blogpost-cta__heading">{{ ts('blogpost_cta_heading', `Bereit für dein ${serviceLabel}-Angebot?`) }}</h2>
                <p class="sv-blogpost-cta__sub">{{ t('blogpost_cta_sub', 'Vergleiche kostenlos Angebote von Fachbetrieben aus deiner Region.') }}</p>
                <Link href="/" class="sv-blogpost-cta__btn">{{ t('blogpost_cta_btn', 'Jetzt kostenlos Angebote erhalten') }}</Link>
            </div>
        </section>
    </AppLayout>
</template>

<style scoped>
.sv-blogpost-hero { position: relative; min-height: 340px; display: flex; align-items: center; background: #0a1628; overflow: hidden; }
.sv-blogpost-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #0a1628, #001d75); }
.sv-blogpost-hero__content { position: relative; z-index: 2; max-width: 720px; margin: 0 auto; padding: 100px 24px 48px; text-align: center; }
.sv-blogpost-hero__bread { font-family: 'Inter', sans-serif; font-size: 12px; color: rgba(255,255,255,0.4); margin-bottom: 20px; }
.sv-blogpost-hero__bread a { color: rgba(255,255,255,0.5); text-decoration: none; }
.sv-blogpost-hero__bread a:hover { color: #fff; }
.sv-blogpost-hero__tags { display: flex; justify-content: center; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
.sv-blogpost-hero__tag-pill { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 600; padding: 4px 12px; border-radius: 16px; background: rgba(0,215,130,0.1); color: #00d782; }
.sv-blogpost-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(28px, 4.5vw, 44px); font-weight: 800; color: #fff; line-height: 1.15; margin-bottom: 14px; }
.sv-blogpost-hero__excerpt { font-family: 'Inter', sans-serif; font-size: 16px; color: rgba(255,255,255,0.65); line-height: 1.6; margin-bottom: 12px; }
.sv-blogpost-hero__meta { font-family: 'Inter', sans-serif; font-size: 13px; color: rgba(255,255,255,0.4); }

.sv-blogpost-img-section { background: #f0f4f8; padding: 0 24px 24px; }
.sv-blogpost-img-section__inner { max-width: 900px; margin: 0 auto; }
.sv-blogpost-img { width: 100%; height: clamp(250px, 35vw, 450px); object-fit: cover; border-radius: 16px; }

.sv-blogpost-funnel-strip { padding: 24px 0; background: #f0f4f8; }
.sv-blogpost-funnel-strip__inner { max-width: 520px; margin: 0 auto; padding: 0 24px; }
.sv-funnel-wrap { padding: 24px; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.06); }

.sv-reading-bar { position: fixed; top: 0; left: 0; right: 0; height: 3px; background: #e2e8f0; z-index: 1000; pointer-events: none; }
.sv-reading-bar__fill { height: 100%; background: #00d782; transition: width 100ms linear; box-shadow: 0 0 6px rgba(0,215,130,0.5); }

.sv-article { padding: 0 0 80px; background: #ffffff; }
.sv-article__inner { max-width: 720px; margin: 0 auto; padding: 0 24px; }
.sv-article__card { padding: 36px; background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; margin-top: -24px; position: relative; z-index: 4; }
@media (max-width: 640px) { .sv-article__card { padding: 20px; } }
.sv-article__byline { display: flex; align-items: center; gap: 12px; padding-bottom: 20px; margin-bottom: 24px; border-bottom: 1px solid #e2e8f0; }
.sv-article__byline-avatar { width: 40px; height: 40px; border-radius: 50%; background: #00d782; color: #1a1a1a; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.sv-article__byline-info { display: flex; flex-direction: column; gap: 2px; }
.sv-article__byline-name { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: #1a1a1a; }
.sv-article__byline-date { font-family: 'Inter', sans-serif; font-size: 12px; color: #94a3b8; }
.sv-article__prose { font-family: 'Inter', sans-serif; font-size: 16px; color: #1a1a1a; line-height: 1.8; }
.sv-article__prose :deep(h2) { font-size: 24px; font-weight: 700; margin-top: 2em; margin-bottom: 0.6em; color: #1a1a1a; }
.sv-article__prose :deep(h3) { font-size: 20px; font-weight: 600; margin-top: 1.5em; margin-bottom: 0.5em; color: #1a1a1a; }
.sv-article__prose :deep(p) { margin-bottom: 1.15em; }
.sv-article__prose :deep(a) { color: #001d75; text-decoration: underline; }
.sv-article__prose :deep(blockquote) { border-left: 3px solid #00d782; padding-left: 1.25em; color: #64748b; font-style: italic; margin: 1.5em 0; }
.sv-article__prose :deep(img) { border-radius: 12px; margin: 1.5em 0; max-width: 100%; }
.sv-article__prose :deep(ul), .sv-article__prose :deep(ol) { padding-left: 1.5em; margin-bottom: 1em; }
.sv-article__prose :deep(li) { margin-bottom: 0.5em; }

.sv-related { margin-top: 48px; }
.sv-related__heading { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; color: #1a1a1a; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #e2e8f0; }
.sv-related__grid { display: grid; gap: 16px; grid-template-columns: 1fr; }
@media (min-width: 640px) { .sv-related__grid { grid-template-columns: repeat(3, 1fr); } }
.sv-related__card { display: flex; flex-direction: column; border-radius: 12px; overflow: hidden; background: #f0f4f8; text-decoration: none; transition: transform 200ms; }
.sv-related__card:hover { transform: translateY(-3px); }
.sv-related__card-img-wrap { aspect-ratio: 16/9; overflow: hidden; }
.sv-related__card-img { width: 100%; height: 100%; object-fit: cover; }
.sv-related__card-body { padding: 14px; }
.sv-related__card-title { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: #1a1a1a; line-height: 1.35; }

.sv-article__disclaimer { margin-top: 32px; padding: 16px 20px; border-radius: 12px; background: #f0f4f8; border: 1px solid #e2e8f0; }
.sv-article__disclaimer p { font-family: 'Inter', sans-serif; font-size: 13px; color: #64748b; line-height: 1.7; }

.sv-blogpost-cta { padding: 80px 0; background: #0a1628; }
.sv-blogpost-cta__inner { max-width: 700px; margin: 0 auto; padding: 0 24px; text-align: center; }
.sv-blogpost-cta__heading { font-family: 'Inter', sans-serif; font-size: clamp(24px, 3vw, 36px); font-weight: 800; color: #fff; margin-bottom: 12px; }
.sv-blogpost-cta__sub { font-family: 'Inter', sans-serif; font-size: 16px; color: rgba(255,255,255,0.65); margin-bottom: 24px; }
.sv-blogpost-cta__btn { display: inline-flex; padding: 14px 32px; background: #00d782; color: #1a1a1a; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; text-decoration: none; }
.sv-blogpost-cta__btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,215,130,0.3); }
</style>
